import {
  faCheckCircle,
  faTrashAlt,
  faWarning,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { CellContext } from "@tanstack/table-core";
import { useNavigate } from "react-router-dom";
import { validate as validateUUID } from "uuid";

import { DisplayDateFromNow } from "src/base-components/Date";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { TagPill } from "src/base-components/TagPill";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { TemplateStatus, useTemplateStatus } from "src/cases/hooks";
import { useCaseTemplateWithVersions } from "src/cases/queries/templates";
import {
  CaseQueueColumnSchema,
  DeserializedQueueCase,
} from "src/cases/queues/types";
import { getTemplateIcon } from "src/cases/templates/utils";
import { EntityDetailsWindowPill } from "src/entities/entityView/EntityDetailsWindowPill";
import { Environment } from "src/hooks/useEnvironment";
import {
  DashboardPageParams,
  getUrlToCaseTemplateVersionPage,
} from "src/router/urls";
import { DateType } from "src/utils/datetime";
import { formatNumber } from "src/utils/numbers";
import { assertUnreachable } from "src/utils/typeUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const CaseTemplatePill: React.FC<{
  templateId: string;
  versionId: string;
}> = ({ templateId, versionId }) => {
  const { wsId, orgId } = useParamsDecode<DashboardPageParams>();
  const { data: template, isLoading: isLoadingTemplate } =
    useCaseTemplateWithVersions(templateId);
  const version = template?.versions?.find((v) => v.id === versionId);

  const navigate = useNavigate();

  if (isLoadingTemplate) {
    return <SkeletonPlaceholder height="h-5.5" width="w-16" />;
  }

  if (!template || !version) return null;

  const icon = getTemplateIcon(template.attributes.icon);

  return (
    <Pill
      size="sm"
      variant="gray"
      onClick={(e) => {
        e.stopPropagation();
        navigate(
          getUrlToCaseTemplateVersionPage(orgId, wsId, templateId, versionId),
        );
      }}
    >
      {icon && <Pill.Icon icon={icon} />}
      <Pill.Text>
        {template.attributes.template_name}, {version.attributes.version_name}
      </Pill.Text>
    </Pill>
  );
};

const statusToPillVariant = {
  [TemplateStatus.Outdated]: "orange",
  [TemplateStatus.Deleted]: "red",
  [TemplateStatus.Active]: "green",
} as const;

const statusToStatusIcon = {
  [TemplateStatus.Outdated]: faWarning,
  [TemplateStatus.Deleted]: faTrashAlt,
  [TemplateStatus.Active]: faCheckCircle,
};

export const CaseTemplateStatusValue: React.FC<{
  value: {
    templateId: string;
    schemaVersion: number;
    versionId: string;
    queueId?: string;
  };
}> = ({ value: { templateId, versionId, queueId, schemaVersion } }) => {
  const { status, isLoading } = useTemplateStatus({
    templateId,
    versionId,
    queueId,
    schemaVersion,
  });

  if (isLoading) {
    return <SkeletonPlaceholder height="h-full" width="w-10" />;
  }

  if (!status) return null;

  const icon = statusToStatusIcon[status];
  const variant = statusToPillVariant[status];

  return (
    <Pill size="sm" variant={variant ?? "gray"}>
      <Pill.Icon icon={icon} />
      <Pill.Text>{status}</Pill.Text>
    </Pill>
  );
};

export const CasePropertyValue: React.FC<{
  columnSchema: CaseQueueColumnSchema;
  environment: Environment;
  info: CellContext<DeserializedQueueCase, unknown>;
}> = ({ columnSchema, environment, info }) => {
  return renderPropertyValueByType(columnSchema, environment, info);
};

const renderPropertyValueByType = (
  columnSchema: CaseQueueColumnSchema,
  environment: Environment,
  info: CellContext<DeserializedQueueCase, unknown>,
): React.ReactNode => {
  const value = info.getValue();
  if (typeof value === "undefined") return null;

  switch (columnSchema.type) {
    case "string": {
      if (typeof value === "string" && validateUUID(value)) {
        return <TruncatedIdPill id={value} />;
      }
      return <span className="font-inter-normal-12px">{String(value)}</span>;
    }

    case "enum": {
      const enumValue = columnSchema.options.find((v) => v.value === value);
      return <TagPill size="sm" value={enumValue || String(value)} />;
    }

    case "datetime": {
      return (
        <span className="font-inter-normal-12px">
          <DisplayDateFromNow value={value as DateType} allowFuture />
        </span>
      );
    }

    case "tags": {
      if (Array.isArray(value)) {
        return (
          <div className="flex gap-1">
            {value.map((tag, index) => {
              const tagValue = columnSchema.options.find(
                (v) => v.value === tag,
              );
              return <TagPill key={index} value={tagValue || String(tag)} />;
            })}
          </div>
        );
      }

      return <span className="font-inter-normal-12px">{String(value)}</span>;
    }

    case "entity_ref": {
      const entityId = value as string;
      const displayName =
        info.table.options.meta?.actions?.getEntityDisplayValue?.(entityId) ||
        entityId;

      const getPropertyIcon = info.table.options.meta?.actions?.getPropertyIcon;

      return (
        <EntityDetailsWindowPill
          entityIcon={
            getPropertyIcon?.(columnSchema) as IconDefinition | undefined
          }
          environment={environment}
          id={entityId}
          name={displayName}
          schemaId={columnSchema.entity_type}
        />
      );
    }
    case "event_ref": {
      return <TruncatedIdPill id={String(value)} />;
    }

    case "integer":
    case "number":
      return (
        <span className="font-inter-normal-12px">
          {typeof value === "number" ? formatNumber(value) : String(value)}
        </span>
      );

    default:
      assertUnreachable(columnSchema);
  }
};
